import { AxiosResponse } from 'axios';

import { LiveStatus } from '@/application/commons/enums/live.enum';
import { ClassroomDto } from '@/application/interfaces/classroom/dtos/get-classroom-by-topic';
import { CollaborationResult } from '@/infrastructure/gateways/classroom/models/collaboration-result';

export interface PegasusGatewayResponse<T = any> {
  data?: T;
  error: AxiosResponse | undefined;
  identifier?: string | undefined;
}

export interface IPegasusGateway {
  getIdentifiedUser(): Promise<CollaborationResult<UserDto>>;
  getClassroomByCourseGuid(guid: string): Promise<PegasusGatewayResponse<ClassroomDto>>;
  recordVideoProgress(
    guidVideo: string,
    usage: number,
    student: string,
  ): Promise<PegasusGatewayResponse>;
  recordLessonProgress(
    guidCourse: string,
    guidVideo: string,
    usage: number,
    student: string,
  ): Promise<PegasusGatewayResponse>;
  recordVideoWatching(
    topicGuid: string,
    lessonGuid: string,
    videoGuid: string,
    watchingLocation: string,
  ): Promise<PegasusGatewayResponse>;
  GetCoursesForClassroom(): Promise<CollaborationResult<CoursesForClassroomDto[]>>;
  getStudyPlan(guid: string): Promise<CollaborationResult<StudyPlanDto>>;
  getStreamingByGuid({
    courseGuid,
    streamingGuid,
  }: GetStreamingByGuidInput): Promise<CollaborationResult<getStreamingByGuidResponse>>;
}

export enum LessonType {
  LIVE = 'LIVE',
  PRE_RECORDED = 'PRE_RECORDED',
}

export type ClassifierDto = {
  guid: string;
  name: string;
  classificationLevel: ClassificationLevelDto;
  childClassifiers: ClassifierDto[];
  courses: CourseDto[];
};

export type ClassificationLevelDto = {
  guid: string;
  name: string;
};

export type CourseDto = {
  guid: string;
  name: string;
  slug: string;
  description?: string;
  lessons?: LessonDto[];
  isEnabled: boolean;
  canBeEnabled?: boolean;
  totalLessons?: number;
  totalHours?: number;
  thumbnail: string;
  classifiers: ClassifierDto[];
  isDeleted?: boolean;
  creationMoment: Date;
  isCompleted?: boolean;
  percentualProgress?: number;
};

export type CoursesForClassroomDto = {
  guid: string;
};

export type StudyPlanDto = {
  guid: string;
  classificationLevel: ClassificationLevelDto;
  weeks: WeeksDto[];
  startDate: Date;
  totalWeeks: number;
  totalLessons: number;
};

export type WeeksDto = {
  order: number;
  planned: CourseDto[];
};

export type LessonDto = {
  guid: string;
  slug: string;
  name: string;
  teacher: string;
  nextLessonGuid?: string;
  previousLessonGuid?: string;
  videos?: Array<VideoDto>;
  streaming?: StreamingDto;
  isCompleted: boolean;
  exercises?: Array<ExercisesDto>;
  supportMaterial?: SupportMaterialDto;
  progress?: number;
  duration?: number;
  lessonType: string;
  order: number;
  thumbnail?: string;
};

export type ExerciseDto = {
  guid: string;
  url: string;
};

export type ExercisesDto = {
  guid: string;
  lessonGuid: string;
  introduction?: string;
};

export type SupportMaterialDto = {
  guid: string;
  url: string;
};

export type VideoDto = {
  guid: string;
  lessonGuid: string;
  title: string;
  kalturaId: string;
  nextVideoGuid?: string;
  previousVideoGuid?: string;
  isCompleted: boolean;
  thumbUrl: string;
  progress?: number;
  duration?: number;
};

export type StreamingDto = {
  guid: string;
  lessonGuid: string;
  title: string;
  streamingAccountId: string;
  streamingId: string;
  startStreaming: Date;
  endStreaming: Date;
  order: number;
  lessonStatus: LiveStatus;
  description?: string;
  streamingProvider: StreamingProviderType;
};

export type UserDto = {
  guid: string;
  name: string;
  email: string;
  permissions: {
    canViewAdmin: boolean;
  };
};

export type CertificateEnabledDto = {
  enabled: boolean;
  coursePercentualProgress: number;
  courseMinimalPercentageNeeded: number;
};

export type GetStreamingByGuidInput = {
  courseGuid: string;
  streamingGuid: string;
};

export type getStreamingByGuidResponse = {
  lesson: LessonDto;
};

export enum StreamingProviderType {
  LIVESTREAM = 'LIVESTREAM',
  YOUTUBE = 'YOUTUBE',
}
